import { apiBase } from './apiBase';

/** As duas imagens da marca: o logotipo completo (horizontal) e o ícone quadrado (trilho de ícones e aba). */
export type BrandImage = 'logo' | 'icon';

function brandSrc(kind: BrandImage, slug: string | undefined, url: unknown): string | null {
  if (!slug || typeof url !== 'string' || !url) return null;
  let h = 0;
  for (let i = 0; i < url.length; i++) h = (h * 31 + url.charCodeAt(i)) | 0;
  const base = apiBase();
  return `${base}/api/v1/public/tenants/${encodeURIComponent(slug)}/${kind}?v=${(h >>> 0).toString(36)}`;
}

/**
 * Endereço do logotipo completo da empresa servido pelo próprio núcleo (que busca o https da marca e guarda em cache, ou
 * entrega o enviado). A console só carrega imagens do próprio endereço (política de conteúdo); o parâmetro v muda quando
 * o endereço da marca muda. Vai na entrada, no topo e nos cabeçalhos; nunca no quadradinho do trilho.
 */
export function logoSrc(slug: string | undefined, url: unknown): string | null {
  return brandSrc('logo', slug, url);
}

/** Ícone quadrado da marca (só enviado pela console, "upload:<resumo>"): trilho de ícones e ícone da aba. */
export function iconSrc(slug: string | undefined, url: unknown): string | null {
  return typeof url === 'string' && url.startsWith('upload:') ? brandSrc('icon', slug, url) : null;
}

const LOGO_TYPES = new Set(['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml']);
const dataUris = new Map<string, Promise<string | null>>();

/**
 * Logotipo completo como data URI, para ir embutido nos arquivos exportados (HTML interativo, página do mapa), que abrem
 * sem rede. Busca uma vez por endereço; sem logotipo, tipo estranho, mais de 2 MB ou erro, devolve null (o arquivo mostra o nome).
 */
export function logoDataUri(src: string | null, load: typeof fetch = (...a) => fetch(...a)): Promise<string | null> {
  if (!src) return Promise.resolve(null);
  const hit = dataUris.get(src);
  if (hit) return hit;
  const p = (async () => {
    try {
      const r = await load(src, { credentials: 'omit' });
      if (!r.ok) return null;
      const blob = await r.blob();
      const type = (blob.type || r.headers.get('content-type') || '').split(';')[0]!.trim().toLowerCase();
      if (!LOGO_TYPES.has(type) || blob.size === 0 || blob.size > 2_000_000) return null;
      const buf = new Uint8Array(await blob.arrayBuffer());
      let bin = '';
      for (let i = 0; i < buf.length; i += 0x8000) bin += String.fromCharCode(...buf.subarray(i, i + 0x8000));
      return `data:${type};base64,${btoa(bin)}`;
    } catch {
      return null;
    }
  })();
  dataUris.set(src, p);
  // falha não fica guardada: a próxima exportação tenta de novo
  void p.then((v) => {
    if (v == null) dataUris.delete(src);
  });
  return p;
}
